/*
 *        ___           __                                         __       __
 *       /   |  __  __ / /_ ____   _____ ____   ____ ___   ____   / /___   / /_ ___
 *      / /| | / / / // __// __ \ / ___// __ \ / __ `__ \ / __ \ / // _ \ / __// _ \
 *     / ___ |/ /_/ // /_ / /_/ // /__ / /_/ // / / / / // /_/ // //  __// /_ /  __/
 *    /_/  |_|\__,_/ \__/ \____/ \___/ \____//_/ /_/ /_// .___//_/ \___/ \__/ \___/
 *                                                     /_/
 *
 *** HTML
 * To auto initialize the element the input has to be of type 'search' and has to have the class 'autocomplete'.
 * The Autocomplete object will be accessible through `document.getElementById('customer').ac`.
 * If Autocomplete couldn't initialize for whatever reason, it will tell you in your browsers Javascript console.
 *
 * Hint: Autocomplete can also be initialized without using data attributes (see section Javascript below).
 *
 * All settings can be set by HTML attributes:
 *
 * Attribute      | Function
 * -------------- | --------
 * data-id        | (optional) The id used for the hidden input which will be added by Autocomplete when an item was picked.
 * data-name      | The name used for the hidden input which will be added by Autocomplete when an item has been picked.
 * data-href      | The endpoint which provides the data to display, the search query will be appended depending on the type of call (see API response).
 * data-container | The selector for the element, the items will be rendered into.
 * data-value     | The value to preselect.
 * data-mode      | (optional) The pick mode, possible values `single`, `multi` (default: `single`)
 *
 * Example:
 * <div class="form-group col-md-6">
 *     <label>Customer</label>
 *     <input type="search" class="form-control autocomplete" id="customer" data-name="customer" data-href="/customer/ajax/search" data-container="#customer-autocomplete">
 *     <div id="customer-autocomplete" class="autocomplete-container"></div>
 * </div>
 *
 *
 *** API response
 * Autocomplete expects two API endpoints to work properly, the search endpoint and the preselect endpoint.
 *
 * -> Search endpoint
 * The search endpoint is used to get the results the user can pick from.
 * Autocomplete takes the `href` given and appends `?q=` or `&q=` (depending on the url) plus the search word.
 *
 * Expected response:
 * {
 *     data: [
 *         {
 *             "id": 123,
 *             "title": 'Item title',
 *             "description": 'Item description'
 *         }, {
 *             ...
 *         }
 *     ],
 *     meta: {}
 * ]
 *
 * -> Preselect endpoint
 * The preselect endpoint is used to preselect a specific item.
 * Using the `value` given and appends `?id=` or `&id=` (depending on the url) plus the `value`.
 *
 * Expected response:
 * {
 *     data: {
 *         "id": 123,
 *         "title": "Item title",
 *         "description": "Item description"
 *     },
 *     meta: {}
 * }
 *
 *
 *** Events
 * It's possible to register a callback function to run if an item has been picked.
 * The callback gets the picked object passed as the first parameter.
 * To access the Autocomplete object use this syntax `window.$autocomplete['<id of autocomplete element>']`
 *
 * Example:
 * window.$autocomplete['customer'].onPick(function(data) {
 *     console.log(data)
 * })
 *
 *
 * Javascript
 * Autocomplete can also be initialized programmatically.
 *
 * Syntax:
 * new Autocomplete(input: String|HTMLElement, container: String|HTMLElement, url: String, name: String)
 *
 * Example:
 * const autocomplete = new Autocomplete('#customer', '#customer-autocomplete', '/customer/ajax/search?q=', 'customer')
 */

class Autocomplete {
    debug = false
    index = 0
    inputElm
    containerElm
    listElm
    resultsElm
    listItemElms = []
    href
    id
    name
    mode
    data = []
    mouseOverList = false
    shouldHideList = false
    callbacks = {}

    innerTpl(id, title, description, status) {

        // Title Element
        const titleElm = document.createElement('DIV')
        titleElm.className = 'autocomplete__list__item__title'
        titleElm.innerText = title

        // Description
        const descriptionElm = document.createElement('DIV')
        descriptionElm.className = 'autocomplete__list__item__description'
        descriptionElm.innerText = description

        // List Item Element
        const elm = document.createElement('DIV')
        elm.className = 'autocomplete__list__item'
        elm.dataset.id = id
        elm.addEventListener('click', this.onListItemClickEvent.bind(this))

        if (status === 'active' || status === 'disabled')
            elm.classList.add(`autocomplete__list__item--${status}`)

        elm.appendChild(titleElm)
        elm.appendChild(descriptionElm)

        return elm
    }

    resultTpl(id, title, description, invalid) {

        // Input
        const inputElm = document.createElement('INPUT')
        inputElm.type = 'hidden'
        if (this.id !== undefined)
            inputElm.id = this.id
        inputElm.name = this.name
        inputElm.value = id

        // Action
        const actionElm = document.createElement('BUTTON')
        actionElm.type = 'button'
        actionElm.className = 'btn btn-sm btn-danger autocomplete-result__action'
        actionElm.innerText = '×'
        actionElm.addEventListener('click', this.onResultActionClickEvent.bind(this))

        // Title
        const titleElm = document.createElement('DIV')
        titleElm.className = 'autocomplete-result__title'
        titleElm.innerText = title

        // Description
        const descriptionElm = document.createElement('DIV')
        descriptionElm.className = 'autocomplete__description'
        descriptionElm.innerText = description

        // Result Element
        const elm = document.createElement('DIV')
        elm.className = 'autocomplete-result'
        if (invalid) {
            elm.classList.add('border')
            elm.classList.add('border-danger')
        }
        elm.appendChild(inputElm)
        elm.appendChild(actionElm)
        elm.appendChild(titleElm)
        elm.appendChild(descriptionElm)

        return elm
    }

    initContainer() {
        this.listElm = document.createElement('DIV')
        this.listElm.className = 'autocomplete__list'
        this.hideList()

        this.resultsElm = document.createElement('DIV')
        this.resultsElm.className = 'autocomplete__results'

        this.containerElm.appendChild(this.listElm)
        this.containerElm.appendChild(this.resultsElm)

        this.containerElm.classList.add(`autocomplete-container--${this.mode}`)
    }

    onFocusEvent(e) {
        if (this.debug)
            console.log('Focus!')
        this.showList()
    }

    onBlurEvent(e) {
        if (this.debug)
            console.log('Blur!')
        if (this.mouseOverList) {
            this.shouldHideList = true
        } else {
            this.hideList()
        }
    }

    onInputEvent(e) {
        if (this.debug)
            console.log('Input!')
        if (e.target.value.length < 3) {
            this.resetList()
            return
        }
        this.getList(e.target.value)
    }

    onKeyDownEvent(e) {
        if (this.debug)
            console.log('KeyDown!')
        switch (e.keyCode) {
            case 38: // Up
                e.preventDefault()
                if (this.index <= 0)
                    break
                this.index--
                this.selectIndex()
                break
            case 40: // Down
                e.preventDefault()
                if (this.index >= this.data.length - 1)
                    break
                this.index++
                this.selectIndex()
                break
            case 13: // Enter
                e.preventDefault()
                if (this.listItemElms[this.index])
                    this.listItemElms[this.index].dispatchEvent(new Event('click'))
                break
            case 27: // Esc
                this.resetList()
                this.hideList()
                break
        }
    }

    onMouseEnterEvent(e) {
        if (this.debug)
            console.log('Mouse Enter!')
        this.mouseOverList = true
    }

    onMouseLeaveEvent(e) {
        if (this.debug)
            console.log('Mouse Leave!')
        this.mouseOverList = false
        if (this.shouldHideList)
            this.hideList()
    }

    onListItemClickEvent(e) {
        e.stopPropagation()
        this.inputElm.value = ''
        this.inputElm.dispatchEvent(new Event('focus'))
        if (this.debug)
            console.log('Item clicked!')
        const selectedData = this.data.data.find((f) => e.currentTarget.dataset.id === f.id)
        if (!selectedData) {
            console.error('[Autocomplete] Selected invalid item! How did you even do that?!')
            return
        }
        this.resetList()
        this.hideList()

        if (this.callbacks.onPick instanceof Function)
            this.callbacks.onPick.bind(this)(selectedData)

        // Select value, remove search box, show Tag like box
        if (this.mode === 'single')
            this.unregisterEvents()
        this.mouseOverList = false
        if (this.mode === 'single') {
            this.inputElm.style.display = 'none'
            this.resetResults()
        }
        this.resultsElm.appendChild(this.resultTpl(selectedData.id, selectedData.title, selectedData.description, this.inputElm.classList.contains('is-invalid')))
    }

    onResultActionClickEvent(e) {
        e.preventDefault()
        if (this.debug)
            console.log('Result Action Click!')

        e.target.closest('.autocomplete-result').remove()

        if (this.mode === 'single')
            this.inputElm.style.display = 'block'

        if (this.callbacks.onUnpick instanceof Function)
            this.callbacks.onUnpick.bind(this)()

        this.inputElm.focus()
        if (this.mode === 'single')
            this.registerEvents()
    }

    onPick(cb) {
        if (!(cb instanceof Function)) {
            console.error('[Autocomplete] This is not a function!')
            return
        }
        this.callbacks.onPick = cb
        return this
    }

    onUnpick(cb) {
        if (!(cb instanceof Function)) {
            console.error('[Autocomplete] This is not a function!')
            return
        }
        this.callbacks.onUnpick = cb
        return this
    }

    getList(query) {
        this.loading()
        const separator = this.href.includes('?') ? '&' : '?'
        axios.get(`${this.href}${separator}q=${query || ''}`)
            .then(res => {
                this.index = 0
                this.data = res.data
                this.renderList()
            }).catch(err => {
                this.resetList()
                console.error(err)
            })
    }

    getResult(id) {
        this.loading()
        const separator = this.href.includes('?') ? '&' : '?'
        axios.get(`${this.href}${separator}id=${id || ''}`)
            .then(res => {
                this.resetList()
                this.hideList()
                this.mouseOverList = false
                if (this.mode === 'single') {
                    this.unregisterEvents()
                    this.inputElm.style.display = 'none'
                }

                if (this.callbacks.onPick instanceof Function)
                    this.callbacks.onPick.bind(this)(res.data.data)

                if (this.mode === 'single') {
                    this.resetResults()
                }
                this.resultsElm.appendChild(this.resultTpl(res.data.data.id, res.data.data.title, res.data.data.description || '', this.inputElm.classList.contains('is-invalid')))
            })
            .catch(err => {
                this.resetList()
                console.error(err)
            })
    }

    loading() {
        this.listElm.innerHTML = ''

        // Icon
        const iconElm = document.createElement('I')
        iconElm.className = "fa fa-spinner fa-spin"

        // List Item
        const itemElm = document.createElement('DIV')
        itemElm.className = 'autocomplete__list__item autocomplete__list__item--loading'
        itemElm.appendChild(iconElm)

        this.listElm.appendChild(itemElm)
    }

    selectIndex() {
        const selectedItem = this.listItemElms[this.index]

        if (!selectedItem)
            return

        // Remove active class
        const items = this.listElm.querySelectorAll('.autocomplete__list__item--active')
        for (const item of items) {
            item.classList.remove('autocomplete__list__item--active')
        }

        // Scroll to the right position so that the item is in viewport
        if (selectedItem.offsetTop < this.listElm.scrollTop) { // Out top
            this.listElm.scrollTop = selectedItem.offsetTop
        } else if (selectedItem.offsetTop + selectedItem.clientHeight > this.listElm.clientHeight + this.listElm.scrollTop) { // Out bottom
            this.listElm.scrollTop = selectedItem.offsetTop - this.listElm.clientHeight + selectedItem.clientHeight
        }

        // Add active class
        selectedItem.classList.add('autocomplete__list__item--active')
    }

    renderList() {
        this.listElm.innerHTML = ''
        let idx = 0
        this.listItemElms = []
        for (const item of this.data.data) {
            const itemElm = this.innerTpl(item.id, item.title, item.description, this.index === idx ? 'active' : undefined)
            this.listItemElms.push(itemElm)
            this.listElm.appendChild(itemElm)
            idx++
        }
        this.showList()
    }

    resetList() {
        this.index = 0
        this.data = [];
        this.listItemElms = []
        this.listElm.innerHTML = ''
    }

    showList() {
        this.listElm.style.display = 'block'
        this.shouldHideList = false
    }

    hideList() {
        this.listElm.style.display = 'none'
        this.shouldHideList = false
    }

    resetResults() {
        this.resultsElm.innerHTML = ''
    }

    showResults() {
        this.resultsElm.style.display = 'block'
    }

    hideResults() {
        this.resultsElm.style.display = 'none'
    }

    preselectValue(value) {
        this.getResult(value)
    }

    registerEvents() {
        this.inputElm.addEventListener('focus', this.onFocusEvent.bind(this))
        this.inputElm.addEventListener('blur', this.onBlurEvent.bind(this))
        this.inputElm.addEventListener('input', _.debounce(this.onInputEvent.bind(this), 300))
        this.inputElm.addEventListener('keydown', this.onKeyDownEvent.bind(this))
        this.listElm.addEventListener('mouseenter', this.onMouseEnterEvent.bind(this))
        this.listElm.addEventListener('mouseleave', this.onMouseLeaveEvent.bind(this))
    }

    unregisterEvents() {
        const newInputElm = this.inputElm.cloneNode(true)
        this.inputElm.parentNode.replaceChild(newInputElm, this.inputElm)
        this.inputElm = newInputElm

        const newListElm = this.listElm.cloneNode(true)
        this.listElm.parentNode.replaceChild(newListElm, this.listElm)
        this.listElm = newListElm
    }

    constructor(input, container, href, name, value, mode, id) {

        // Input
        this.inputElm = input
        if (typeof this.inputElm === 'string')
            this.inputElm = document.querySelector(input)
        if (!(this.inputElm instanceof Element)) {
            console.error('[Autocomplete] `input` not defined!')
            return
        }

        // Container
        this.containerElm = container
        if (this.containerElm === undefined && this.inputElm.dataset.container)
            this.containerElm = this.inputElm.dataset.container
        if (typeof this.containerElm === 'string')
            this.containerElm = document.querySelector(this.containerElm)
        if (!(this.containerElm instanceof Element)) {
            console.error('[Autocomplete] `container` is not defined!')
            return
        }

        // ID
        this.id = id
        if (this.id === undefined && this.inputElm.dataset.id)
            this.id = this.inputElm.dataset.id

        // Name
        this.name = name
        if (this.name === undefined && this.inputElm.dataset.name)
            this.name = this.inputElm.dataset.name
        else {
            console.error('[Autocomplete] `data-name` not set on input filed!')
            return
        }

        // Href
        this.href = href
        if (this.href === undefined && this.inputElm.dataset.href)
            this.href = this.inputElm.dataset.href
        else {
            console.error('[Autocomplete] `data-href` not set on input field!')
            return
        }

        // Mode
        this.mode = mode
        if (this.mode === undefined && this.inputElm.dataset.mode)
            this.mode = this.inputElm.dataset.mode
        if (this.mode === undefined)
            this.mode = 'single'
        if (this.mode !== 'single' && this.mode !== 'multi') {
            console.error(`[Autocomplete] \`data-mode\` invalid option given: ${this.mode}`)
            return
        }

        // Value
        if (value === undefined && this.inputElm.dataset.value)
            value = this.inputElm.dataset.value
        if (this.mode === 'multi') {
            value = JSON.parse(value)
            if (!value.length)
                value = undefined
        }

        // Init container elements
        this.initContainer()

        // Events
        this.registerEvents()

        if (value !== undefined)
            this.preselectValue(value)
    }

    destroy() {
        this.resetList()
        this.inputElm.style.display = 'none'
        this.showList()
        this.unregisterEvents()
        window.$autocomplete[this.inputElm.id] = undefined
    }
}
module.exports = Autocomplete
